import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatedDisclosure } from '@/components/AnimatedDisclosure';
import styles from './settings-rows.module.css';

/**
 * An open settings section: a titled group of rows. Cards are what the search reports as
 * "where" a setting lives, so a card title here should match the `card` field of
 * the matching entries in the shared settings catalog.
 */
export function SettingsCard({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  /** Disclosure rows for the rarely-touched things, rendered under the rows. */
  footer?: ReactNode;
}) {
  return (
    <section className={styles.card}>
      <h2 className={styles.cardHead}>{title}</h2>
      <div className={styles.rows}>{children}</div>
      {footer}
    </section>
  );
}

/**
 * One setting, with its current value on the right.
 *
 * The value is the whole point: a contractor should be able to read a settings
 * page and know what everything is set to without opening a single control. Pass
 * `value` for a summary the row can render itself, or `control` for a live
 * control (a toggle, a segmented picker) that belongs inline.
 *
 * `id` is the anchor the settings search scrolls to, and must match the entry's
 * `anchor` in the shared catalog — a pairing test enforces it.
 */
export function SettingsRow({
  id,
  label,
  hint,
  value,
  control,
  onOpen,
  disabled,
  stacked,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  value?: ReactNode;
  control?: ReactNode;
  /** Makes the whole row activate, for rows that lead somewhere. */
  onOpen?: () => void;
  disabled?: boolean;
  /** Puts the control under the label — for a textarea, which never fits beside one. */
  stacked?: boolean;
}) {
  const body = (
    <>
      <span className={styles.rowLabel}>
        <span className={styles.rowTitle}>{label}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      {control ? (
        <span className={styles.rowControl}>{control}</span>
      ) : (
        <span className={styles.rowValue}>
          {value ?? <span className={styles.rowUnset}>Not set</span>}
          {onOpen ? <span className={styles.chevron} aria-hidden="true">›</span> : null}
        </span>
      )}
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        id={id}
        className={styles.row}
        onClick={onOpen}
        disabled={disabled}
        data-setting-row
      >
        {body}
      </button>
    );
  }

  return (
    <div
      id={id}
      className={[styles.row, stacked ? styles.rowStacked : ''].join(' ')}
      data-setting-row
      aria-disabled={disabled || undefined}
    >
      {body}
    </div>
  );
}

/**
 * The one switch every settings page uses. Before this, three pages each styled
 * their own `accent-color` checkbox, so the same control looked different
 * depending on which tab you were on.
 */
export function SettingsToggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name, since the row's visible label is a sibling. */
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={[styles.switch, checked ? styles.switchOn : ''].join(' ')}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.knob} aria-hidden="true" />
    </button>
  );
}

/**
 * A rarely-touched group, collapsed by default. Present, not hidden: progressive
 * disclosure means what a contractor sets once at onboarding sits behind one
 * click rather than competing with what they change weekly.
 *
 * The body always grows on the grid-rows recipe (AnimatedDisclosure) rather
 * than mounting in one frame; it still unmounts once closed, so a half-typed
 * form is discarded the same as before.
 */
export function SettingsDisclosure({
  id,
  label,
  note,
  open,
  onToggle,
  children,
}: {
  id?: string;
  label: string;
  note?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();
  return (
    <div className={styles.disclosureWrap}>
      <button
        type="button"
        id={id}
        className={styles.disclosure}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className={[styles.discChevron, open ? styles.discChevronOpen : ''].join(' ')} aria-hidden="true">
          ›
        </span>
        <span className={styles.discCopy}>
          {label}
          {note ? <span className={styles.discNote}>{note}</span> : null}
        </span>
      </button>
      <AnimatedDisclosure id={panelId} open={open}>
        <div className={styles.disclosureBody}>{children}</div>
      </AnimatedDisclosure>
    </div>
  );
}

/**
 * Privacy and Terms, centred at the foot of every settings page.
 *
 * It used to appear on three of the twelve, which read as arbitrary. The Account
 * workspace keeps its own copy in the identity pane, where it suits that page's
 * split layout; every other settings page ends with this.
 */
export function SettingsLegal() {
  return (
    <div className={styles.legal}>
      <a href="/privacy" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
        Privacy Policy
      </a>
      <a href="/terms" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
        Terms of Service
      </a>
    </div>
  );
}

/**
 * Bring the row named in the URL hash into view and flash it.
 *
 * The settings search and the command palette both navigate to `route#anchor`.
 * Without this the page would load at the top and leave the contractor hunting
 * for the control they just searched for. The flash is what makes it obvious
 * which of a dozen rows was meant; it is opacity-only and off under reduced
 * motion.
 */
export function useSettingAnchor(): { openFor: string | null } {
  const { hash, state } = useLocation();
  // The search stamps a fresh key on every selection, so choosing the same row
  // twice re-runs this. react-router leaves location.hash alone when navigating
  // to the URL you are already on, so the hash by itself never fires twice.
  const nonce = (state as { settingAnchorKey?: number } | null)?.settingAnchorKey ?? null;
  const done = useRef<string | null>(null);
  const [openFor, setOpenFor] = useState<string | null>(null);

  useEffect(() => {
    const id = hash.replace(/^#/, '');
    const token = `${id}:${nonce ?? ''}`;
    if (!id || done.current === token) return;
    done.current = token;
    setOpenFor(id);

    // The row mounts after the page's own query resolves, and several settings
    // pages render a skeleton until then. A fixed 1s of retries was shorter than
    // a cold load on a slow connection, and the failure was silent: the
    // disclosure opened, nothing scrolled, and the search looked broken rather
    // than slow. Watch the DOM instead of guessing at a budget, with a ceiling
    // so the observer cannot outlive the visit.
    let findTimer: number | undefined;
    let flashTimer: number | undefined;
    let giveUpTimer: number | undefined;
    let growTimer: number | undefined;
    let stopWaiting: (() => void) | undefined;
    let observer: MutationObserver | undefined;
    let el: HTMLElement | null = null;

    const arrive = (found: HTMLElement) => {
      stopWaiting?.();
      found.scrollIntoView({ block: 'center', behavior: 'smooth' });
      found.classList.add(styles.flash);
      flashTimer = window.setTimeout(() => found.classList.remove(styles.flash), 1600);
    };

    const land = (found: HTMLElement) => {
      el = found;
      observer?.disconnect();
      if (giveUpTimer) window.clearTimeout(giveUpTimer);
      // A row inside a SettingsDisclosure mounts in the same commit that starts
      // its panel growing from 0fr. Scrolling then centres it against a page
      // that is still short: the scroll clamps, the panel grows below the fold,
      // and the flash plays off screen. So while the panel is shorter than its
      // content, wait for the grow to finish. A panel already open, or one under
      // reduced motion (no transition), is full height and lands at once; the
      // timeout covers a transitionend that never comes.
      const panel = found.closest<HTMLElement>('[data-open]');
      const clip = panel?.firstElementChild;
      if (!panel || panel.dataset.open !== 'true' || !clip || clip.scrollHeight - clip.clientHeight <= 1) {
        arrive(found);
        return;
      }
      const onGrown = (event: TransitionEvent) => {
        if (event.target === panel && event.propertyName === 'grid-template-rows') arrive(found);
      };
      panel.addEventListener('transitionend', onGrown);
      growTimer = window.setTimeout(() => arrive(found), 400);
      stopWaiting = () => {
        panel.removeEventListener('transitionend', onGrown);
        window.clearTimeout(growTimer);
        stopWaiting = undefined;
      };
    };

    const find = () => {
      const found = document.getElementById(id);
      if (found) {
        land(found);
        return true;
      }
      return false;
    };

    if (!find()) {
      observer = new MutationObserver(() => {
        find();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      // Ten seconds covers a cold load on a slow connection; past that the row
      // genuinely is not on this page.
      giveUpTimer = window.setTimeout(() => observer?.disconnect(), 10_000);
    }

    // Leaving the page mid-wait must not leave an observer or a timer running,
    // and two rapid searches must not race to flash different rows.
    return () => {
      observer?.disconnect();
      stopWaiting?.();
      if (findTimer) window.clearTimeout(findTimer);
      if (flashTimer) window.clearTimeout(flashTimer);
      if (giveUpTimer) window.clearTimeout(giveUpTimer);
      el?.classList.remove(styles.flash);
    };
  }, [hash, nonce]);

  /** The anchor a page just arrived at, so it can open the disclosure holding it. */
  return { openFor };
}
